import { useState } from "react";
import { motion } from "framer-motion";
import { Sparkles, Moon, Loader2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import api, { formatApiError } from "../lib/api";
import { StarField } from "../components/StarField";

const HERO = "https://images.pexels.com/photos/17954395/pexels-photo-17954395.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function AuthPage() {
  const { authenticate } = useAuth();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const path = mode === "login" ? "/auth/login" : "/auth/register";
      const payload =
        mode === "login"
          ? { email: form.email, password: form.password }
          : form;
      const { data } = await api.post(path, payload);
      authenticate(data.token, data.user);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const googleSignIn = () => {
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen cosmic-bg grain relative flex items-center justify-center px-4 py-12">
      <div
        className="absolute inset-0 opacity-25"
        style={{ backgroundImage: `url(${HERO})`, backgroundSize: "cover", backgroundPosition: "center" }}
      />
      <div className="absolute inset-0 bg-black/60" />
      <StarField count={90} />

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="glass relative z-10 w-full max-w-md rounded-2xl p-8 sm:p-10"
        data-testid="auth-card"
      >
        <div className="flex flex-col items-center text-center mb-8">
          <div className="floaty mb-4 flex h-14 w-14 items-center justify-center rounded-full border border-[#D4AF37]/40">
            <Moon className="h-6 w-6 gold-text" strokeWidth={1.5} />
          </div>
          <p className="overline gold-text mb-2">Celestial Oracle</p>
          <h1 className="text-5xl font-serif tracking-tight leading-none">Astro Trinity</h1>
          <p className="text-sm text-white/60 mt-3 leading-relaxed">
            Your birth chart across Vedic, Hellenistic &amp; Chinese wisdom — read by AI.
          </p>
        </div>

        <div className="flex rounded-full border border-white/10 p-1 mb-6">
          {["login", "register"].map((m) => (
            <button
              key={m}
              data-testid={`tab-${m}`}
              onClick={() => { setMode(m); setError(""); }}
              className={`flex-1 rounded-full py-2 text-sm font-medium capitalize transition-colors duration-300 ${
                mode === m ? "bg-white/10 text-white" : "text-white/50 hover:text-white/80"
              }`}
            >
              {m === "login" ? "Sign In" : "Create Account"}
            </button>
          ))}
        </div>

        <form onSubmit={submit} className="space-y-4">
          {mode === "register" && (
            <input
              data-testid="name-input"
              required
              placeholder="Your name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full rounded-lg bg-white/5 border border-white/10 px-4 py-3 text-sm outline-none focus:border-[#45F0DF]/50 transition-colors duration-300"
            />
          )}
          <input
            data-testid="email-input"
            required
            type="email"
            placeholder="Email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            className="w-full rounded-lg bg-white/5 border border-white/10 px-4 py-3 text-sm outline-none focus:border-[#45F0DF]/50 transition-colors duration-300"
          />
          <input
            data-testid="password-input"
            required
            type="password"
            placeholder="Password"
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            className="w-full rounded-lg bg-white/5 border border-white/10 px-4 py-3 text-sm outline-none focus:border-[#45F0DF]/50 transition-colors duration-300"
          />

          {error && (
            <p data-testid="auth-error" className="text-sm text-red-400">{error}</p>
          )}

          <button
            data-testid="auth-submit"
            type="submit"
            disabled={loading}
            className="btn-gold w-full rounded-lg py-3 text-sm font-semibold tracking-wide flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            {mode === "login" ? "Enter the Cosmos" : "Begin Your Journey"}
          </button>
        </form>

        <div className="flex items-center gap-4 my-6">
          <span className="h-px flex-1 bg-white/10" />
          <span className="text-xs text-white/30 uppercase tracking-widest">or</span>
          <span className="h-px flex-1 bg-white/10" />
        </div>

        <button
          data-testid="google-signin"
          onClick={googleSignIn}
          className="w-full rounded-lg border border-white/15 bg-white/5 hover:bg-white/10 py-3 text-sm font-medium flex items-center justify-center gap-3 transition-colors duration-300"
        >
          <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="h-5 w-5" />
          Continue with Google
        </button>
      </motion.div>
    </div>
  );
}
